import { jwtDecode } from 'jwt-decode';

/**
 * Cand expira tokenul de acces si cand trebuie reimprospatat, ca functii pure.
 *
 * 🔴 De ce exista (2026-10-07). Reimprospatarea proactiva din `client.ts` citea expirarea DOAR din
 * `TOKEN_EXPIRES_AT_KEY`, iar cheia aceea se scria doar dupa un refresh — niciodata la login, OTP,
 * inscriere. Deci in prima ora de dupa autentificare verificarea raspundea mereu „nu", tokenul
 * expira, iar toate cererile paralele plecau cu el si primeau fiecare cate un 401 inainte de
 * primul refresh.
 *
 * Doua remedii, amandoua aici:
 *   1. expirarea se scrie odata cu ORICE token nou (`expirareLaPrimire`, folosita de
 *      `stocare-token.ts`);
 *   2. plasa de siguranta: cheia lipsa sau necitibila => expirarea se ia din claim-ul `exp` al
 *      tokenului (`expirareCunoscuta`). Acopera sesiunile deschise inainte de reparatie.
 *
 * Modul pur, fara `localStorage` si fara ceas propriu, ca sa fie testabil pe `node`.
 */

/** Cu cat inainte de expirare se cere un token nou. */
export const MARJA_REFRESH_MS = 60 * 1000;

interface ClaimuriDeTimp {
    exp?: unknown;
    iat?: unknown;
}

function secundeValide(valoare: unknown): number | null {
    return typeof valoare === 'number' && Number.isFinite(valoare) && valoare > 0 ? valoare : null;
}

/** Un token stricat, gol sau fara payload nu arunca: nu are claim-uri, atat. */
function claimuriDeTimp(token: string | null | undefined): ClaimuriDeTimp | null {
    if (!token) return null;
    try {
        const payload = jwtDecode<ClaimuriDeTimp>(token);
        return payload && typeof payload === 'object' ? payload : null;
    } catch {
        return null;
    }
}

/** `exp` ca moment absolut, in milisecunde, pe ceasul SERVERULUI. `null` daca nu se poate citi. */
export function expirareDinJwt(token: string | null | undefined): number | null {
    const exp = secundeValide(claimuriDeTimp(token)?.exp);
    return exp === null ? null : exp * 1000;
}

/**
 * Momentul expirarii unui token ABIA PRIMIT, pe ceasul browserului.
 *
 * Se prefera o durata (`expiresIn` din raspuns, altfel `exp - iat` din token) adunata la `acum`,
 * nu `exp` ca atare: `exp` e pe ceasul serverului, iar un telefon cu ceasul dat inainte ar vedea
 * tokenul „expirat" din prima secunda si ar cere un refresh la fiecare cerere. `exp` absolut
 * ramane ultima varianta, pentru un token fara `iat`.
 */
export function expirareLaPrimire(
    token: string | null | undefined,
    acum: number,
    expiresInSecunde?: number | null
): number | null {
    const durata = secundeValide(expiresInSecunde);
    if (durata !== null) return acum + durata * 1000;

    const claimuri = claimuriDeTimp(token);
    const exp = secundeValide(claimuri?.exp);
    if (exp === null) return null;
    const iat = secundeValide(claimuri?.iat);
    if (iat !== null && exp > iat) return acum + (exp - iat) * 1000;
    return exp * 1000;
}

/** Expirarea stocata, daca e un numar; altfel cea din token; altfel `null` (necunoscuta). */
export function expirareCunoscuta(
    token: string | null | undefined,
    expiraLaStocat: string | null | undefined
): number | null {
    const stocat = Number.parseInt(expiraLaStocat ?? '', 10);
    if (Number.isFinite(stocat) && stocat > 0) return stocat;
    return expirareDinJwt(token);
}

export interface StareToken {
    token: string | null | undefined;
    /** Valoarea bruta din `TOKEN_EXPIRES_AT_KEY`: poate lipsi, poate fi `""` (dupa logout). */
    expiraLaStocat: string | null | undefined;
    acum: number;
    marja?: number;
}

/**
 * `true` cand tokenul expira in mai putin de `marja`. Expirare necunoscuta => `false`, ca inainte:
 * cererea pleaca asa cum e, iar un eventual 401 e tratat de interceptorul de raspuns.
 */
export function trebuieReimprospatat({ token, expiraLaStocat, acum, marja = MARJA_REFRESH_MS }: StareToken): boolean {
    const expiraLa = expirareCunoscuta(token, expiraLaStocat);
    if (expiraLa === null) return false;
    return expiraLa - acum < marja;
}
